import { useEffect } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import { useAuthStore } from '@/stores';

/**
 * Error boundary component for React Router.
 * Catches and handles errors that occur during rendering, including
 * the "Rendered fewer hooks than expected" error that can occur during logout.
 */
export function ErrorBoundary() {
    const error = useRouteError() as Error;
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);

    useEffect(() => {
        // If we're not authenticated and get a hook error, redirect to login
        // This handles the race condition during logout where components
        // try to re-render while authentication state is changing
        if (!isAuthenticated && error?.message?.includes('Rendered fewer hooks')) {
            navigate('/login', { replace: true });
        }
    }, [error, isAuthenticated, navigate]);

    // If not authenticated and it's a hook error, show nothing (we're redirecting)
    if (!isAuthenticated && error?.message?.includes('Rendered fewer hooks')) {
        return null;
    }

    // For other errors, show a proper error page
    return (
        <div className='bg-canvas flex h-svh w-full items-center justify-center'>
            <div className='text-center'>
                <h1 className='text-heading-h1 text-dark mb-4'>Oops! Something went wrong</h1>
                <p className='text-body-md text-gray mb-6'>{error?.message || 'An unexpected error occurred'}</p>
                <button
                    onClick={() => navigate('/dashboard')}
                    className='bg-brand-main text-on-brand rounded-lg px-6 py-3 font-semibold hover:opacity-90'
                >
                    Go to Dashboard
                </button>
            </div>
        </div>
    );
}
